import { BinaryAPILike, TickHistoryFormatterLike } from '@smartcharts/core';
import { DERIV_APP_ID } from './config';

export const fetchJson = async (url: string) => {
    const res = await fetch(url);
    return res.json();
};

// Shared WebSocket connection for subscriptions
let sharedWs: WebSocket | null = null;
// callback maps
const subscriptionCallbacks = new Map<string, (response: any) => void>(); // keyed by subscription id OR temp key
const keyToSubscriptionId = new Map<string, string>(); // symbol-granularity -> subscription id
const subscriptionIdToKey = new Map<string, string>(); // subscription id -> symbol-granularity

function getOrCreateSharedWs(): WebSocket {
    if (!sharedWs || sharedWs.readyState === WebSocket.CLOSED || sharedWs.readyState === WebSocket.CLOSING) {
        sharedWs = new WebSocket(`wss://ws.derivws.com/websockets/v3?app_id=${DERIV_APP_ID}`);
        sharedWs.onmessage = (evt) => {
            try {
                const data = JSON.parse(evt.data);
                const subId = data.subscription?.id;
                const echoReq = data.echo_req || {};
                const symbol = echoReq.ticks_history || echoReq.ticks || echoReq.candle;
                const granularity = echoReq.granularity || 0;
                const tempKey = symbol ? `${symbol}-${granularity}` : undefined;

                // Attempt to find callback by subscription id first
                let callback: ((response: any) => void) | undefined;
                if (subId) {
                    callback = subscriptionCallbacks.get(subId);
                    // If callback not yet mapped to subscription id, try temp key
                    if (!callback && tempKey && subscriptionCallbacks.has(tempKey)) {
                        callback = subscriptionCallbacks.get(tempKey);
                        if (callback) {
                            // Promote temp key to subscription id
                            subscriptionCallbacks.delete(tempKey);
                            subscriptionCallbacks.set(subId, callback);
                            keyToSubscriptionId.set(tempKey, subId);
                            subscriptionIdToKey.set(subId, tempKey);
                            console.debug('Promoted subscription key', tempKey, '->', subId);
                        }
                    }
                }

                // If still no callback and we have a tempKey (first response without subscription id?) use it
                if (!callback && tempKey) {
                    callback = subscriptionCallbacks.get(tempKey);
                }

                if (callback) {
                    callback(data);
                } else {
                    // Unrouted data (may be pings or other msg types)
                    if (data.msg_type && ['tick', 'candles'].includes(data.msg_type)) {
                        console.debug('Unrouted WS data for symbol', symbol, 'granularity', granularity, 'msg_type', data.msg_type);
                    }
                }
            } catch (e) {
                console.error('WS message parse error:', e);
            }
        };
        sharedWs.onerror = (err) => {
            console.error('Shared WebSocket error:', err);
        };
        sharedWs.onclose = () => {
            console.log('Shared WebSocket closed');
            sharedWs = null;
        };
    }
    return sharedWs;
}

export const binaryApi: BinaryAPILike = {
    async getTickHistory(params: any): Promise<any> {
        const { symbol, granularity, start, end, count } = params;
        return new Promise((resolve, reject) => {
            const ws = new WebSocket(`wss://ws.derivws.com/websockets/v3?app_id=${DERIV_APP_ID}`);
            let timeout: number | undefined;
            ws.onopen = () => {
                const req: any = {
                    ticks_history: symbol,
                    end: end || 'latest',
                    count: count || 1000,
                    adjust_start_time: 1,
                };
                if (granularity && granularity > 0) {
                    req.granularity = granularity;
                    req.style = 'candles';
                }
                if (start) {
                    req.start = start;
                    delete req.count;
                }
                ws.send(JSON.stringify(req));
                // Start timeout only after request is sent
                timeout = window.setTimeout(() => {
                    try { ws.close(); } catch {}
                    reject(new Error('WebSocket history request timeout'));
                }, 30000);
            };
            ws.onmessage = (evt) => {
                try {
                    const data = JSON.parse(evt.data);
                    if (data.history || data.candles) {
                        if (timeout) window.clearTimeout(timeout);
                        try { ws.close(); } catch {}
                        resolve(data);
                    } else if (data.error) {
                        if (timeout) window.clearTimeout(timeout);
                        try { ws.close(); } catch {}
                        reject(new Error(data.error.message));
                    }
                } catch (e) {
                    if (timeout) window.clearTimeout(timeout);
                    reject(e);
                }
            };
            ws.onerror = (err) => {
                console.error('getTickHistory WebSocket error:', err);
                if (timeout) window.clearTimeout(timeout);
                reject(new Error('WebSocket connection failed'));
            };
            ws.onclose = () => {
                if (timeout) window.clearTimeout(timeout);
            };
        });
    },
    subscribeTickHistory(params: any, callback: (response: any) => void): void {
        const { symbol, granularity } = params;
        const ws = getOrCreateSharedWs();
        const key = `${symbol}-${granularity || 0}`;
        // Clean any prior temp mapping
        subscriptionCallbacks.delete(key);

        // Use tick stream for granularity 0; candle stream otherwise (Deriv API uses 'ticks')
        const req: any = granularity && granularity > 0
            ? { ticks_history: symbol, granularity, style: 'candles', subscribe: 1, end: 'latest', count: 1 }
            : { ticks: symbol, subscribe: 1 };

        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(req));
        else ws.addEventListener('open', () => ws.send(JSON.stringify(req)), { once: true });

        // Temporary callback until subscription id promotion
        const tempCallback = (response: any) => {
            const subId = response.subscription?.id;
            if (subId && !subscriptionIdToKey.has(subId)) {
                keyToSubscriptionId.set(key, subId);
                subscriptionIdToKey.set(subId, key);
                // promote
                subscriptionCallbacks.set(subId, callback);
                subscriptionCallbacks.delete(key);
                console.debug('Subscription established', key, '->', subId);
            }
            callback(response);
        };
        subscriptionCallbacks.set(key, tempCallback);
    },
    forget(params: { symbol: string; granularity: number }): void {
        const key = `${params.symbol}-${params.granularity || 0}`;
        const subId = keyToSubscriptionId.get(key);
        if (!subId || !sharedWs || sharedWs.readyState !== WebSocket.OPEN) return;
        try {
            sharedWs.send(JSON.stringify({ forget: subId }));
            console.debug('Sent forget for subscription', subId, 'key', key);
        } catch (e) {
            console.warn('Failed to send forget', e);
        }
        // cleanup maps
        subscriptionCallbacks.delete(subId);
        subscriptionCallbacks.delete(key);
        keyToSubscriptionId.delete(key);
        subscriptionIdToKey.delete(subId);
    },
};

export const tickFormatter: TickHistoryFormatterLike = {
    formatHistory(response: any) {
        if (response.history) {
            const { prices = [], times = [] } = response.history;
            return prices.map((p: number, i: number) => ({ Close: p, Date: new Date(times[i] * 1000).toISOString().replace('Z', ''), DT: new Date(times[i] * 1000) }));
        }
        if (response.candles) {
            return response.candles.map((c: any) => ({
                Open: c.open,
                High: c.high,
                Low: c.low,
                Close: c.close,
                Date: new Date(c.epoch * 1000).toISOString().replace('Z', ''),
                DT: new Date(c.epoch * 1000),
            }));
        }
        if (response.tick) {
            const t = response.tick;
            return [{ Close: t.quote, Date: new Date(t.epoch * 1000).toISOString().replace('Z', ''), DT: new Date(t.epoch * 1000) }];
        }
        if (response.candle) {
            const c = response.candle;
            return [{ Open: c.open, High: c.high, Low: c.low, Close: c.close, Date: new Date(c.epoch * 1000).toISOString().replace('Z', ''), DT: new Date(c.epoch * 1000) }];
        }
        return [];
    }
};
